import type {
  ScopeActivationItem,
  ScopeActivationSummary,
} from "@/api/scope-activation";

import type { Verdict } from "./verdictMeta";
import { VERDICT_ORDER } from "./verdictMeta";

const DECISION_VERDICTS: Verdict[] = ["needs_decision", "needs_review"];

// Confiança vem do backend em PT (alta/media/baixa). O filtro e as comparações
// (ex.: lowConfidenceItems) casam esse valor cru; a UI exibe o rótulo em inglês.
const CONFIDENCE_LABELS: Record<string, string> = {
  alta: "High",
  media: "Medium",
  baixa: "Low",
};

/** Rótulo EN da confiança (valor cru do backend). "-" quando ausente. */
export function confidenceLabel(v: string | null | undefined): string {
  if (!v) return "-";
  return CONFIDENCE_LABELS[v] ?? v;
}

// Eixo de elegibilidade (sugestão da árvore + veredito do analista): tokens EN
// vindos do backend; o rótulo de tela é resolvido aqui, como no confidence.
const ELIGIBILITY_LABELS: Record<string, string> = {
  activate: "Activate",
  decision: "Decision",
  out_of_scope: "Out of scope",
};

/** Rótulo EN do token de elegibilidade (activate/decision/out_of_scope). */
export function eligibilityLabel(v: string | null | undefined): string {
  if (!v) return "-";
  return ELIGIBILITY_LABELS[v] ?? v;
}

/** Quanto da observação cabe numa célula; quem mostra põe o texto inteiro no `title`. */
const NOTE_PREVIEW_LENGTH = 40;

/** O início da observação de curadoria, com reticências quando ela não cabe (#1344). */
export function notePreview(note: string): string {
  return note.length <= NOTE_PREVIEW_LENGTH
    ? note
    : `${note.slice(0, NOTE_PREVIEW_LENGTH).trimEnd()}…`;
}

// Eixo Sugestão como protagonista do Fit-to-Standard (fluxograma do Marco).
// Ordem canônica + cores da barra/legenda (activate=verde, decision=âmbar,
// out_of_scope=cinza), independentes do VERDICT_META do eixo ECC.
export type Suggestion = "activate" | "decision" | "out_of_scope";
export const SUGGESTION_ORDER: Suggestion[] = [
  "activate",
  "decision",
  "out_of_scope",
];
export const SUGGESTION_META: Record<
  Suggestion,
  { label: string; bar: string }
> = {
  activate: { label: "Activate", bar: "bg-emerald-500" },
  decision: { label: "Decision", bar: "bg-amber-500" },
  out_of_scope: { label: "Out of scope", bar: "bg-gray-300" },
};

// Motivos do gate de país (Passo 0). O toggle "só disponível no país" tira esses
// itens de todas as abas, pra o cliente ver só o que ele pode habilitar no país.
export const OUT_BY_COUNTRY_REASONS = new Set([
  "Not available in country",
  "Not in Availability",
]);

/** Filtra pros itens disponíveis no país (ou devolve tudo quando desligado). */
export function countryAvailableItems(
  items: ScopeActivationItem[],
  countryOnly: boolean,
): ScopeActivationItem[] {
  if (!countryOnly) return items;
  return items.filter(
    (i) => !OUT_BY_COUNTRY_REASONS.has(i.suggestion_reason ?? ""),
  );
}

export type SuggestionCounts = Record<Suggestion, number>;

/** Contagem por Sugestão (fluxograma) num conjunto de itens. */
export function suggestionCounts(items: ScopeActivationItem[]): SuggestionCounts {
  const counts: SuggestionCounts = {
    activate: 0,
    decision: 0,
    out_of_scope: 0,
  };
  for (const it of items) {
    const s = it.suggestion;
    if (s && s in counts) counts[s as Suggestion] += 1;
  }
  return counts;
}

export interface ReasonCount {
  suggestion: Suggestion;
  reason: string;
  count: number;
}

/**
 * "Por que cada decisão": agrupa os itens por (sugestão, motivo) e devolve as
 * contagens ordenadas pela sugestão (activate→decision→out_of_scope) e, dentro
 * de cada uma, por contagem desc. É o bloco que o consultor usa pra justificar.
 */
export function reasonBreakdown(items: ScopeActivationItem[]): ReasonCount[] {
  const map = new Map<string, ReasonCount>();
  for (const it of items) {
    const s = it.suggestion;
    if (!s || !(s in SUGGESTION_META)) continue;
    const reason = it.suggestion_reason || "-";
    const key = `${s}||${reason}`;
    const cur = map.get(key);
    if (cur) cur.count += 1;
    else map.set(key, { suggestion: s as Suggestion, reason, count: 1 });
  }
  const rank = (s: Suggestion) => SUGGESTION_ORDER.indexOf(s);
  return [...map.values()].sort(
    (a, b) =>
      rank(a.suggestion) - rank(b.suggestion) || b.count - a.count,
  );
}

export interface SuggestionLob {
  lob: string;
  total: number;
  counts: SuggestionCounts;
  // itens que precisam de decisão (suggestion === "decision")
  decisions: number;
}

/**
 * Agrega por LoB usando a Sugestão (não o verdict ECC), ordenado por densidade
 * de "decision" (desc; empate por total desc, depois nome).
 */
export function suggestionLobBreakdown(
  items: ScopeActivationItem[],
): SuggestionLob[] {
  const byLob = new Map<string, ScopeActivationItem[]>();
  for (const it of items) {
    const key = it.lob || UNKNOWN_LOB;
    const arr = byLob.get(key);
    if (arr) arr.push(it);
    else byLob.set(key, [it]);
  }
  const out: SuggestionLob[] = [];
  for (const [lob, lobItems] of byLob) {
    const counts = suggestionCounts(lobItems);
    out.push({
      lob,
      total: lobItems.length,
      counts,
      decisions: counts.decision,
    });
  }
  out.sort((a, b) => {
    if (b.decisions !== a.decisions) return b.decisions - a.decisions;
    if (b.total !== a.total) return b.total - a.total;
    return a.lob.localeCompare(b.lob);
  });
  return out;
}

/** True quando o item precisa de ação do analista (decisão ou revisão). */
export function isWorklistItem(item: ScopeActivationItem): boolean {
  return DECISION_VERDICTS.includes(item.verdict);
}

/** Só os itens que precisam de ação (needs_decision + needs_review). */
export function worklistItems(
  items: ScopeActivationItem[],
): ScopeActivationItem[] {
  return items.filter(isWorklistItem);
}

/** Quantas decisões+revisões um summary representa (o "108"). */
export function decisionCount(summary: ScopeActivationSummary): number {
  return (summary.needs_decision ?? 0) + (summary.needs_review ?? 0);
}

/** Contagem por veredito num conjunto de itens. */
export type VerdictCounts = Record<Verdict, number>;

export function countByVerdict(items: ScopeActivationItem[]): VerdictCounts {
  const counts: VerdictCounts = {
    activate: 0,
    needs_decision: 0,
    needs_review: 0,
    skip: 0,
  };
  for (const it of items) {
    if (it.verdict in counts) counts[it.verdict] += 1;
  }
  return counts;
}

export interface LobBreakdown {
  lob: string;
  total: number;
  counts: VerdictCounts;
  // needs_decision + needs_review desta LoB
  decisions: number;
  // proporção de decisões sobre o total da LoB (0..1)
  decisionRatio: number;
}

export const UNKNOWN_LOB = "No LoB";

/**
 * Agrega itens por LoB, ORDENADO por densidade de decisão (decisions desc;
 * empate desbloqueia por total desc, depois nome). Puro/testável.
 */
export function lobBreakdowns(items: ScopeActivationItem[]): LobBreakdown[] {
  const byLob = new Map<string, ScopeActivationItem[]>();
  for (const it of items) {
    const key = it.lob || UNKNOWN_LOB;
    const arr = byLob.get(key);
    if (arr) arr.push(it);
    else byLob.set(key, [it]);
  }

  const out: LobBreakdown[] = [];
  for (const [lob, lobItems] of byLob) {
    const counts = countByVerdict(lobItems);
    const decisions = counts.needs_decision + counts.needs_review;
    out.push({
      lob,
      total: lobItems.length,
      counts,
      decisions,
      decisionRatio: lobItems.length > 0 ? decisions / lobItems.length : 0,
    });
  }

  out.sort((a, b) => {
    if (b.decisions !== a.decisions) return b.decisions - a.decisions;
    if (b.total !== a.total) return b.total - a.total;
    return a.lob.localeCompare(b.lob);
  });
  return out;
}

export interface LobMap {
  // LoBs com densidade de decisão relevante (>= minDecisions), em ordem
  hot: LobBreakdown[];
  // resto colapsado numa linha "Outras N LoBs"
  rest: LobBreakdown[];
}

/**
 * Separa as LoBs "quentes" (com >= minDecisions decisões) das demais, que
 * agrupam numa linha resumo. Default minDecisions=2 (LoBs com 0-1 decisão
 * colapsam, conforme o mockup).
 */
export function splitLobMap(
  breakdowns: LobBreakdown[],
  minDecisions = 2,
): LobMap {
  const hot: LobBreakdown[] = [];
  const rest: LobBreakdown[] = [];
  for (const b of breakdowns) {
    if (b.decisions >= minDecisions) hot.push(b);
    else rest.push(b);
  }
  return { hot, rest };
}

/** Itens com confiança baixa (sinal de "2ª olhada"). */
export function lowConfidenceItems(
  items: ScopeActivationItem[],
): ScopeActivationItem[] {
  return items.filter((i) => i.confidence === "baixa");
}

/** Fit rate = activate / total, em 0..100 (0 quando total=0). */
export function fitRatePct(summary: ScopeActivationSummary): number {
  if (!summary.total) return 0;
  return Math.round((summary.activate / summary.total) * 100);
}

/**
 * Ordena uma worklist DENTRO de um grupo: clean_core no topo, depois
 * confiança baixa primeiro. Puro/testável. Não muta o array de entrada.
 */
export function sortWorklistGroup(
  items: ScopeActivationItem[],
): ScopeActivationItem[] {
  const confRank = (c: string): number =>
    c === "baixa" ? 0 : c === "media" ? 1 : c === "alta" ? 2 : 3;
  return [...items].sort((a, b) => {
    if (a.clean_core !== b.clean_core) return a.clean_core ? -1 : 1;
    const cr = confRank(a.confidence) - confRank(b.confidence);
    if (cr !== 0) return cr;
    return a.scope_item_id.localeCompare(b.scope_item_id);
  });
}

export interface WorklistGroup {
  lob: string;
  items: ScopeActivationItem[];
  decisions: number;
}

/**
 * Agrupa SÓ os itens de worklist por LoB, em ordem de densidade de decisão
 * (desc), com cada grupo internamente ordenado (clean_core/confiança baixa
 * no topo). Puro/testável.
 */
export function worklistGroups(items: ScopeActivationItem[]): WorklistGroup[] {
  const work = worklistItems(items);
  const order = lobBreakdowns(work).map((b) => b.lob);
  const byLob = new Map<string, ScopeActivationItem[]>();
  for (const it of work) {
    const key = it.lob || UNKNOWN_LOB;
    const arr = byLob.get(key);
    if (arr) arr.push(it);
    else byLob.set(key, [it]);
  }
  return order.map((lob) => {
    const grp = sortWorklistGroup(byLob.get(lob) ?? []);
    return { lob, items: grp, decisions: grp.length };
  });
}

/**
 * Worklist unificada no eixo Sugestão: os itens **suggestion === "decision"**
 * (fora do baseline) — o que o analista confirma com o cliente. Agrupa por LoB,
 * ordenado por quantidade de decisões (desc), com clean_core/baixa confiança no
 * topo dentro de cada grupo. Substitui o eixo ECC (needs_decision/needs_review)
 * para dar UM significado a "Decision".
 */
export function decisionGroups(items: ScopeActivationItem[]): WorklistGroup[] {
  const work = items.filter((i) => i.suggestion === "decision");
  const byLob = new Map<string, ScopeActivationItem[]>();
  for (const it of work) {
    const key = it.lob || UNKNOWN_LOB;
    const arr = byLob.get(key);
    if (arr) arr.push(it);
    else byLob.set(key, [it]);
  }
  const groups = [...byLob.entries()].map(([lob, arr]) => ({
    lob,
    items: sortWorklistGroup(arr),
    decisions: arr.length,
  }));
  groups.sort((a, b) => b.decisions - a.decisions || a.lob.localeCompare(b.lob));
  return groups;
}

/** Verdetos presentes num breakdown, na ordem canônica (pra renderizar barras). */
export function orderedVerdicts(): Verdict[] {
  return VERDICT_ORDER;
}
